import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { makeTwoWayObject } from "../../helpers/helpers";
import ReactFlagsSelect from "react-flags-select";
import { lang } from "../../utilities/lang";
import { Visibility } from "../../assets/Visibility";
import { VisibilityOff } from "../../assets/VisibilityOff";
import { useState } from "react";

type SimpleInputProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    fieldName: Path<T>;
    label: string;
    required: string | boolean;
    pattern?: { value: RegExp; message: string };
    onBlur?: (event: any) => void;
    className?: string;
    disabled?: boolean;
};

type PasswordInputProps<T extends FieldValues> = SimpleInputProps<T> & {
    validate?: () => boolean | string;
    minLength?: number;
};

type PhoneInputProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    label: string;
    required: string | boolean;
    pattern?: { value: RegExp; message: string };
    onBlur?: (event: any) => void;
    className?: string;
};

type SelectInputProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    fieldName: Path<T>;
    label: string;
    required: string | boolean;
    options: Array<{ key: string; value: string }>;
    className?: string;
    multiple?: boolean;
};

export const TextInput = <T extends FieldValues>(props: SimpleInputProps<T>) => {
    const error = props.form.formState.errors[props.fieldName];
    return (
        <label
            aria-label={props.label}
            className={`${props.fieldName} ${props.className} ${props.disabled ? "disabled" : ""}`}
        >
            <input
                className={`${error ? "input-error" : ""}`}
                type={"text"}
                disabled={props.disabled}
                {...props.form.register(props.fieldName, {
                    required: props.required,
                    pattern: props.pattern,
                    onBlur: props.onBlur,
                })}
            />
            {error && <span className={"error"}>{error.message?.toString()}</span>}
        </label>
    );
};

export const NumberInput = <T extends FieldValues>(props: SimpleInputProps<T>) => {
    const error = props.form.formState.errors[props.fieldName];
    return (
        <label
            aria-label={props.label}
            className={`${props.fieldName} ${props.className} ${props.disabled ? "disabled" : ""}`}
        >
            <input
                className={`${error ? "input-error" : ""}`}
                type={"number"}
                disabled={props.disabled}
                {...props.form.register(props.fieldName, {
                    required: props.required,
                    valueAsNumber: true,
                    onBlur: props.onBlur,
                })}
            />
            {error && <span className={"error"}>{error.message?.toString()}</span>}
        </label>
    );
};

export const SelectInput = <T extends FieldValues>(props: SelectInputProps<T>) => {
    const error = props.form.formState.errors[props.fieldName];
    return (
        <label aria-label={props.label} className={props.className}>
            <select
                className={`${error ? "input-error" : ""}`}
                {...props.form.register(props.fieldName, {
                    required: props.required,
                })}
                multiple={props.multiple ?? false}
            >
                {!props.multiple ? <option value={""}></option> : null}
                {props.options.map((option) => {
                    return (
                        <option key={option.key} value={option.value}>
                            {option.value}
                        </option>
                    );
                })}
            </select>
            {error && <span className={"error"}>{error.message?.toString()}</span>}
        </label>
    );
};

export const PhoneInput = <T extends FieldValues>(props: PhoneInputProps<T>) => {
    const numberCodeMapper = makeTwoWayObject({
        AL: "355",
        AD: "376",
        AM: "374",
        AT: "43",
        AZ: "994",
        BY: "375",
        BE: "32",
        BA: "387",
        BG: "359",
        CH: "41",
        CY: "357",
        CZ: "420",
        DE: "49",
        DK: "45",
        EE: "372",
        ES: "34",
        FI: "358",
        FR: "33",
        GB: "44",
        GE: "995",
        GR: "30",
        HR: "385",
        HU: "36",
        IE: "353",
        IS: "354",
        IT: "39",
        LI: "423",
        LT: "370",
        LU: "352",
        LV: "371",
        MC: "377",
        MD: "373",
        ME: "382",
        MK: "389",
        MT: "356",
        NL: "31",
        NO: "47",
        PL: "48",
        PT: "351",
        RO: "40",
        RS: "381",
        RU: "7",
        SE: "46",
        SI: "386",
        SK: "421",
        SM: "378",
        TR: "90",
        UA: "380",
        VA: "379"
    });
    const exchangeValue = (key: string | number) => {
        return numberCodeMapper[key];
    };

    const tel1 = "tel1" as Path<T>;
    const tel2 = "tel2" as Path<T>;

    return (
        <label className={"phone-input"} aria-label={props.label}>
            <Controller
                name={tel1}
                control={props.form.control}
                render={({ field }) => (
                    <ReactFlagsSelect
                        className={"number-select"}
                        countries={[
                            "AL", "AD", "AM", "AT", "AZ", "BY", "BE", "BA", "BG", "CH", "CY", "CZ", "DE",
                            "DK", "EE", "ES", "FI", "FR", "GB", "GE", "GR", "HR", "HU", "IE", "IS", "IT",
                            "LI", "LT", "LU", "LV", "MC", "MD", "ME", "MK", "MT", "NL", "NO", "PL", "PT",
                            "RO", "RS", "RU", "SE", "SI", "SK", "SM", "TR", "UA", "VA"
                        ]}
                        selected={field.value ? exchangeValue(field.value) : "DE"}
                        customLabels={{
                            AL: "+355",
                            AD: "+376",
                            AM: "+374",
                            AT: "+43",
                            AZ: "+994",
                            BY: "+375",
                            BE: "+32",
                            BA: "+387",
                            BG: "+359",
                            CH: "+41",
                            CY: "+357",
                            CZ: "+420",
                            DE: "+49",
                            DK: "+45",
                            EE: "+372",
                            ES: "+34",
                            FI: "+358",
                            FR: "+33",
                            GB: "+44",
                            GE: "+995",
                            GR: "+30",
                            HR: "+385",
                            HU: "+36",
                            IE: "+353",
                            IS: "+354",
                            IT: "+39",
                            LI: "+423",
                            LT: "+370",
                            LU: "+352",
                            LV: "+371",
                            MC: "+377",
                            MD: "+373",
                            ME: "+382",
                            MK: "+389",
                            MT: "+356",
                            NL: "+31",
                            NO: "+47",
                            PL: "+48",
                            PT: "+351",
                            RO: "+40",
                            RS: "+381",
                            RU: "+7",
                            SE: "+46",
                            SI: "+386",
                            SK: "+421",
                            SM: "+378",
                            TR: "+90",
                            UA: "+380",
                            VA: "+379"
                        }}
                        onSelect={async (code) => {
                            field.onChange(exchangeValue(code));
                            await props.form.trigger(tel1);
                        }}
                        rfsKey={tel1}
                    />
                )}
            />
            <input
                className={`tel-number ${props.form.formState.errors[tel2] ? "input-error" : ""}`}
                type="text"
                placeholder={"123456"}
                autoComplete={"tel2"}
                {...props.form.register(tel2, {
                    required: props.required ? `${props.label} ${lang.get("erforderlich")}` : false,
                    onBlur: () => {
                        props.form.trigger(tel2);
                    },
                    pattern: {
                        value: /^(0|[1-9]\d*)(\.\d+)?$/,
                        message: "Telefonnummer darf nur Zahlen enthalten",
                    },
                })}
            />
            {props.form.formState.errors[tel2] && (
                <span className={"error"}>{props.form.formState.errors[tel2].message as string}</span>
            )}
        </label>
    );
};

export const PasswordInput = <T extends FieldValues>(props: PasswordInputProps<T>) => {
    const error = props.form.formState.errors[props.fieldName];
    const [inputType, setInputType] = useState<"password" | "text">("password");

    const p = "password" as Path<T>;
    const pc = "password_confirmation" as Path<T>;

    return (
        <label
            className={`password-input ${props.fieldName} ${props.className} ${props.disabled ? "disabled" : ""}`}
            aria-label={props.label}
        >
            <div className={"password-wrapper"}>
                <input
                    className={`${props.form.formState.errors[props.fieldName] ? "input-error" : ""}`}
                    type={inputType}
                    autoComplete={"new-password"}
                    disabled={props.disabled}
                    {...props.form.register(props.fieldName, {
                        required: `${props.label} ${lang.get("erforderlich")}`,
                        minLength: props.minLength
                            ? {
                                  value: props.minLength,
                                  message: `${lang.get("Mindestlänge: ")}${props.minLength}`,
                              }
                            : undefined,
                        validate: props.validate,
                        onBlur: () => {
                            props.form.trigger([p, pc]);
                        },
                    })}
                />
                <button
                    type={"button"}
                    tabIndex={-1}
                    className={"password-visibility"}
                    onClick={() => {
                        if (inputType === "password") {
                            setInputType("text");
                        } else {
                            setInputType("password");
                        }
                    }}
                >
                    {inputType === "password" ? <Visibility /> : <VisibilityOff />}
                </button>
            </div>
            {error && <span className={"error"}>{error.message?.toString()}</span>}
        </label>
    );
};
